import React, { useState } from 'react';
import { Button, Text, Stack, Group, Alert, Code, Paper, Title, Container, List } from '@mantine/core';
import { IconAlertTriangle, IconRefresh, IconTrash } from '@tabler/icons-react';
import { clearAuthState } from '../../utils/authUtils';
import { authApi } from '../../api/auth';

/**
 * Component for troubleshooting authentication issues
 * 
 * This component provides buttons to perform various auth-related tasks:
 * - Clear all authentication state
 * - Test token acquisition
 * - Login again
 * 
 * It also shows common reasons for auth issues and tips.
 */
export const AuthTroubleshooter: React.FC = () => {
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  const handleClearAuth = async () => {
    setLoading(true);
    setMessage(null);
    setError(null);
    
    try {
      await clearAuthState();
      setMessage('Authentication state cleared successfully. Try logging in again.');
    } catch (err) {
      setError(`Failed to clear auth state: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogin = async () => {
    setLoading(true);
    setMessage('Redirecting to login...');
    setError(null);
    
    try {
      await authApi.login();
      // Note: This won't execute due to redirect
    } catch (err) {
      setError(`Login failed: ${err instanceof Error ? err.message : String(err)}`);
      setLoading(false);
    }
  };
  
  return (
    <Container size="md" py="xl" mt={20} sx={{ minHeight: 'calc(100vh - 60px)' }}>
      <Paper p="xl" shadow="md" radius="md" withBorder>
        <Stack spacing="lg">
          <Title order={2}>Authentication Troubleshooter</Title>
          
          <Alert icon={<IconAlertTriangle size={16} />} title="Common Auth Issues" color="yellow">
            Authentication issues often happen due to:
            <List size="sm" mt="xs">
              <List.Item>Browser privacy settings blocking cookies</List.Item>
              <List.Item>Cached or corrupted authentication state</List.Item>
              <List.Item>Incompatible browser extensions</List.Item>
              <List.Item>Network or firewall issues</List.Item>
              <List.Item>Recent password changes</List.Item>
            </List>
          </Alert>
          
          {message && (
            <Alert title="Status" color="green">
              {message}
            </Alert>
          )}
          
          {error && (
            <Alert title="Error" color="red">
              {error}
            </Alert>
          )}
          
          <Group position="center">
            <Button 
              leftIcon={<IconTrash size={18} />}
              onClick={handleClearAuth}
              loading={loading}
              color="red"
              variant="outline"
            >
              Clear Auth State
            </Button>
            
            <Button 
              leftIcon={<IconRefresh size={18} />}
              onClick={handleLogin}
              loading={loading}
              color="blue"
            >
              Login Again
            </Button>
          </Group>
          
          <Text size="sm" color="dimmed">
            If you continue to experience issues, try:
            <List size="xs" mt="xs">
              <List.Item>Using a private/incognito browser window</List.Item>
              <List.Item>Disabling browser extensions</List.Item>
              <List.Item>Clearing browser cookies and cache</List.Item>
              <List.Item>Using a different browser</List.Item>
              <List.Item>Contacting your administrator</List.Item>
            </List>
          </Text>
          
          <Paper p="md" bg="gray.0" withBorder>
            <Text size="sm" weight={500}>Technical Details</Text>
            <Code block mt="xs" fz="xs">
              cacheLocation: {localStorage.getItem('msal.cache.location') || 'sessionStorage'}{'\n'}
              storeAuthStateInCookie: true{'\n'}
              browser: {navigator.userAgent}
            </Code>
          </Paper>
        </Stack>
      </Paper>
    </Container>
  );
}; 